<!-- FlipCard.vue - 3D翻转名片子组件（支持双二维码纵向排列） -->
<template>
  <div class="business-card-container" :class="{ flipped: isFlipped }" @click="handleCardClick">
    <div class="card-flipper">
      <!-- 正面 -->
      <div class="card-front">
        <div class="tech-stripe" :class="{ 'second-stripe': cardType === 'second' }"></div>
        <div class="binary-bg">{{ cardData.binaryFront }}</div>
        <div class="card-inner">
          <div class="front-layout">
            <!-- 左侧主要个人信息 -->
            <div class="front-main">
              <div class="avatar-icon">
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="white" width="48px" height="48px">
                  <path :d="cardData.avatarSvg.split(' ')[0]" />
                  <rect x="8" y="10" width="8" height="2" fill="white" opacity="0.8"/>
                  <circle cx="12" cy="14" r="1.5" fill="#2dd4bf" stroke="white"/>
                </svg>
              </div>
              <div class="name">{{ cardData.name }}</div>
              <div class="title">{{ cardData.title }}</div>
              <div class="divider"></div>
              
              <div class="info-row">
                <span class="info-icon">📧</span>
                <span class="info-text">{{ cardData.email }}</span>
              </div>
              <div class="info-row">
                <span class="info-icon">📱</span>
                <span class="info-text">{{ cardData.phone }}</span>
              </div>
              <div class="info-row" v-for="(link, idx) in cardData.links" :key="idx">
                <span class="info-icon">{{ link.icon }}</span>
                <span class="info-text">{{ link.text }}</span>
              </div>
              <div class="info-row">
                <span class="info-icon">📍</span>
                <span class="info-text">{{ cardData.location }}</span>
              </div>
              
              <div class="skill-tags">
                <span class="skill-tag" v-for="(skill, idx) in cardData.skills" :key="idx">{{ skill }}</span>
              </div>
              <div class="badge-text">{{ cardData.badge }}</div>
            </div>
            
            <!-- 右侧二维码区域 - 纵向双二维码 -->
            <div class="front-qr-area vertical-qr">
              <!-- 二维码1：微信 -->
              <div class="qr-item" @click.stop="handleWechatQrClick">
                <div class="qr-image-wrapper">
                  <img src="../image/2.jpg" class="qr-img" alt="微信二维码">
                </div>
                <span class="qr-caption">📱 扫码添加微信好友</span>
              </div>
              <!-- 二维码2：作品集 -->
              <div class="qr-item" @click.stop="handlePortfolioQrClick">
                <div class="qr-image-wrapper">
                  <img src="../image/123.jpg" class="qr-img" alt="作品集二维码">
                </div>
                <span class="qr-caption">📱 扫码添加QQ好友</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- 背面 -->
      <div class="card-back">
        <div class="tech-stripe" :style="backStripeStyle"></div>
        <div class="binary-bg" style="bottom: 15px; right: 15px; font-size: 10px;">{{ cardData.binaryBack }}</div>
        <div class="card-inner">
          <div>
            <div class="back-header">
              <div class="back-icon">
                <svg xmlns="http://www.w3.org/2000/svg" width="28" height="28" fill="white" viewBox="0 0 24 24">
                  <path :d="cardData.backIconSvg" />
                </svg>
              </div>
              <div class="back-title">学术·毕设·技术使命</div>
            </div>
            
            <div class="quote">
              {{ cardData.education }}<br>
              主修课程：{{ cardData.courses }}
            </div>
            
            <div class="exp-section">
              <div class="info-row" style="margin-bottom: 10px;" v-for="(exp, idx) in cardData.experiences" :key="'exp'+idx">
                <span class="info-icon">{{ exp.icon }}</span>
                <span class="info-text"><strong>{{ exp.text }}</strong></span>
              </div>
              <div class="info-row" style="margin-bottom: 10px;" v-for="(project, idx) in cardData.projects" :key="'proj'+idx">
                <span class="info-icon">🏆</span>
                <span class="info-text">{{ project }}</span>
              </div>
              <div class="info-row" style="margin-bottom: 10px;">
                <span class="info-icon">💡</span>
                <span class="info-text">研究兴趣: {{ cardData.interests }}</span>
              </div>
            </div>
            
            <div class="social-links">
              <div class="social-item" v-for="(link, idx) in cardData.socialLinks" :key="idx">{{ link }}</div>
            </div>
          </div>
          <div class="footer-note">{{ cardData.footer }}</div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'FlipCard',
  props: {
    cardData: {
      type: Object,
      required: true
    },
    cardType: {
      type: String,
      default: 'first'
    },
    // 微信二维码图片地址（外部传入）
    wechatQrUrl: {
      type: String,
      default: ''
    },
    // 作品集二维码图片地址（外部传入）
    portfolioQrUrl: {
      type: String,
      default: ''
    }
  },
  data() {
    return {
      isFlipped: false,
      isTouchDevice: false,
      // 默认二维码图片（使用 base64 占位图，实际使用时替换为真实图片地址）
      defaultWechatQr: 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100"%3E%3Crect width="100" height="100" fill="%23ffffff" rx="8"/%3E%3Crect x="10" y="10" width="18" height="18" fill="%230f2b3d" rx="3"/%3E%3Crect x="12" y="12" width="14" height="14" fill="%23ffffff" rx="2"/%3E%3Crect x="15" y="15" width="8" height="8" fill="%230f2b3d" rx="1"/%3E%3Crect x="72" y="10" width="18" height="18" fill="%230f2b3d" rx="3"/%3E%3Crect x="74" y="12" width="14" height="14" fill="%23ffffff" rx="2"/%3E%3Crect x="77" y="15" width="8" height="8" fill="%230f2b3d" rx="1"/%3E%3Crect x="10" y="72" width="18" height="18" fill="%230f2b3d" rx="3"/%3E%3Crect x="12" y="74" width="14" height="14" fill="%23ffffff" rx="2"/%3E%3Crect x="15" y="77" width="8" height="8" fill="%230f2b3d" rx="1"/%3E%3Crect x="38" y="28" width="8" height="8" fill="%231e7b8f"/%3E%3Crect x="50" y="28" width="8" height="8" fill="%232dd4bf"/%3E%3Crect x="62" y="28" width="8" height="8" fill="%230f5c6b"/%3E%3Crect x="28" y="40" width="8" height="8" fill="%231f7b8c"/%3E%3Crect x="40" y="40" width="8" height="8" fill="%230f2b3d"/%3E%3Crect x="52" y="40" width="8" height="8" fill="%232dd4bf"/%3E%3Crect x="64" y="40" width="8" height="8" fill="%231e3a4d"/%3E%3Crect x="28" y="52" width="8" height="8" fill="%232c7a7b"/%3E%3Crect x="40" y="52" width="8" height="8" fill="%230f5c6b"/%3E%3Crect x="52" y="52" width="8" height="8" fill="%231f7b8c"/%3E%3Crect x="64" y="52" width="8" height="8" fill="%232dd4bf"/%3E%3Crect x="38" y="64" width="8" height="8" fill="%23115e67"/%3E%3Crect x="50" y="64" width="8" height="8" fill="%230a2b3e"/%3E%3Crect x="62" y="64" width="8" height="8" fill="%232dd4bf"/%3E%3Ctext x="38" y="96" font-size="5" fill="%236c9a9f"%3E微信%3C/text%3E%3C/svg%3E',
      defaultPortfolioQr: 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100"%3E%3Crect width="100" height="100" fill="%23ffffff" rx="8"/%3E%3Crect x="10" y="10" width="18" height="18" fill="%230f2b3d" rx="3"/%3E%3Crect x="12" y="12" width="14" height="14" fill="%23ffffff" rx="2"/%3E%3Crect x="15" y="15" width="8" height="8" fill="%230f2b3d" rx="1"/%3E%3Crect x="72" y="10" width="18" height="18" fill="%230f2b3d" rx="3"/%3E%3Crect x="74" y="12" width="14" height="14" fill="%23ffffff" rx="2"/%3E%3Crect x="77" y="15" width="8" height="8" fill="%230f2b3d" rx="1"/%3E%3Crect x="10" y="72" width="18" height="18" fill="%230f2b3d" rx="3"/%3E%3Crect x="12" y="74" width="14" height="14" fill="%23ffffff" rx="2"/%3E%3Crect x="15" y="77" width="8" height="8" fill="%230f2b3d" rx="1"/%3E%3Crect x="38" y="28" width="8" height="8" fill="%231e7b8f"/%3E%3Crect x="50" y="28" width="8" height="8" fill="%232dd4bf"/%3E%3Crect x="62" y="28" width="8" height="8" fill="%230f5c6b"/%3E%3Crect x="28" y="40" width="8" height="8" fill="%231f7b8c"/%3E%3Crect x="40" y="40" width="8" height="8" fill="%230f2b3d"/%3E%3Crect x="52" y="40" width="8" height="8" fill="%232dd4bf"/%3E%3Crect x="64" y="40" width="8" height="8" fill="%231e3a4d"/%3E%3Crect x="28" y="52" width="8" height="8" fill="%232c7a7b"/%3E%3Crect x="40" y="52" width="8" height="8" fill="%230f5c6b"/%3E%3Crect x="52" y="52" width="8" height="8" fill="%231f7b8c"/%3E%3Crect x="64" y="52" width="8" height="8" fill="%232dd4bf"/%3E%3Crect x="38" y="64" width="8" height="8" fill="%23115e67"/%3E%3Crect x="50" y="64" width="8" height="8" fill="%230a2b3e"/%3E%3Crect x="62" y="64" width="8" height="8" fill="%232dd4bf"/%3E%3Ctext x="35" y="96" font-size="5" fill="%236c9a9f"%3E作品集%3C/text%3E%3C/svg%3E'
    };
  },
  computed: {
    backStripeStyle() {
      if (this.cardType === 'second') {
        return {
          background: 'linear-gradient(90deg, #1b5e6b, #f59e0b, #0a3b44, #d97706)'
        };
      }
      return {
        background: 'linear-gradient(90deg, #1b5e6b, #2dd4bf, #0a3b44, #1f7b8c)'
      };
    },
    wechatQrSrc() {
      return this.wechatQrUrl || this.defaultWechatQr;
    },
    portfolioQrSrc() {
      return this.portfolioQrUrl || this.defaultPortfolioQr;
    }
  },
  mounted() {
    // 检测触摸屏
    this.isTouchDevice = ('ontouchstart' in window || navigator.maxTouchPoints);
    
    // 同步高度
    this.$nextTick(() => {
      this.syncHeight();
    });
    
    window.addEventListener('resize', this.syncHeight);
    
    // 使用 ResizeObserver 监听高度变化
    if (window.ResizeObserver) {
      const frontCard = this.$el.querySelector('.card-front');
      if (frontCard) {
        this.resizeObserver = new ResizeObserver(() => {
          this.syncHeight();
        });
        this.resizeObserver.observe(frontCard);
      }
    }
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.syncHeight);
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
    }
  },
  methods: {
    handleCardClick() {
      this.isFlipped = !this.isFlipped;
    },
    handleWechatQrClick(e) {
      e.stopPropagation();
      // 微信二维码点击事件，可以打开微信名片或提示
      this.$emit('wechat-click', this.cardData.wechatUrl || '');
    },
    handlePortfolioQrClick(e) {
      e.stopPropagation();
      this.$emit('qr-click', this.cardData.portfolioUrl);
    },
    syncHeight() {
      const frontCard = this.$el.querySelector('.card-front');
      const backCard = this.$el.querySelector('.card-back');
      if (frontCard && backCard) {
        const frontHeight = frontCard.offsetHeight;
        backCard.style.height = frontHeight + 'px';
      }
    }
  }
};
</script>

<style scoped>
.business-card-container {
  perspective: 1800px;
  width: 100%;
  max-width: 680px;
  cursor: pointer;
}

.card-flipper {
  position: relative;
  width: 100%;
  height: auto;
  transition: transform 0.8s cubic-bezier(0.23, 1, 0.32, 1);
  transform-style: preserve-3d;
  border-radius: 28px;
  box-shadow: 0 25px 40px -12px rgba(0, 0, 0, 0.35), 0 0 0 1px rgba(255, 255, 255, 0.2) inset;
}

.business-card-container:hover .card-flipper,
.business-card-container.flipped .card-flipper {
  transform: rotateY(180deg);
}

.card-front, .card-back {
  position: relative;
  backface-visibility: hidden;
  border-radius: 28px;
  overflow: hidden;
  background: #ffffff;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
}

.card-front {
  transform: rotateY(0deg);
  z-index: 2;
}

.card-back {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  transform: rotateY(180deg);
  z-index: 1;
}

.card-inner {
  padding: 32px 28px;
  height: 100%;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
}

.front-layout {
  display: flex;
  gap: 24px;
  flex-wrap: wrap;
}

.front-main {
  flex: 2;
  min-width: 180px;
}

/* 右侧二维码区域 - 纵向双二维码布局 */
.front-qr-area {
  flex: 1;
  min-width: 140px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-start;
  gap: 20px;
  padding-top: 10px;
  border-left: 1px dashed #cbd5e1;
  padding-left: 20px;
}

.vertical-qr {
  flex-direction: column;
}

.qr-item {
  text-align: center;
  display: flex;
  flex-direction: column;
  align-items: center;
  cursor: pointer;
  transition: transform 0.2s ease;
}

.qr-item:hover {
  transform: translateY(-2px);
}

.qr-image-wrapper {
  width: 150px;
  height: 150px;
  background: #f0f7fa;
  border-radius: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 6px 14px rgba(0, 0, 0, 0.08);
  margin-bottom: 8px;
  overflow: hidden;
  background: white;
  padding: 6px;
  transition: transform 0.2s ease;
}

.qr-image-wrapper:hover {
  transform: scale(1.02);
}

.qr-img {
  width: 100%;
  height: 100%;
  object-fit: contain;
  border-radius: 10px;
}

.qr-caption {
  font-size: 15px;
  color: #2c7a7b;
  font-weight: 500;
  background: #eef2ff;
  padding: 4px 10px;
  border-radius: 30px;
  display: inline-block;
}

.qr-sub {
  font-size: 9px;
  color: #5f9ea0;
  margin-top: 4px;
}

@media (max-width: 550px) {
  .front-layout {
    flex-direction: column;
  }
  .front-qr-area {
    border-left: none;
    border-top: 1px dashed #cbd5e1;
    padding-left: 0;
    padding-top: 20px;
    margin-top: 10px;
    flex-direction: row;
    justify-content: space-around;
    align-items: center;
    gap: 16px;
  }
  .vertical-qr {
    flex-direction: row;
    flex-wrap: wrap;
  }
  .qr-image-wrapper {
    width: 80px;
    height: 80px;
  }
}

.tech-stripe {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 6px;
  background: linear-gradient(90deg, #0a2b3e, #1e7b8f, #2dd4bf, #0f5c6b);
  z-index: 3;
}

.tech-stripe.second-stripe {
  background: linear-gradient(90deg, #1b5e6b, #f59e0b, #0a3b44, #d97706);
}

.binary-bg {
  position: absolute;
  bottom: 20px;
  right: 20px;
  font-family: monospace;
  font-size: 12px;
  color: rgba(15, 45, 60, 0.06);
  font-weight: bold;
  white-space: pre;
  line-height: 1.2;
  pointer-events: none;
  z-index: 0;
}

.avatar-icon {
  width: 80px;
  height: 80px;
  background: linear-gradient(135deg, #0f2b3d, #1b6b7c);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 22px;
  box-shadow: 0 12px 20px -8px rgba(0, 0, 0, 0.2);
  border: 2px solid rgba(255, 255, 240, 0.5);
}

.avatar-icon svg {
  width: 48px;
  height: 48px;
  filter: drop-shadow(0 2px 4px rgba(0, 0, 0, 0.2));
}

.name {
  font-size: 28px;
  font-weight: 700;
  letter-spacing: -0.3px;
  background: linear-gradient(120deg, #0f2b3d, #0e5e6e);
  background-clip: text;
  -webkit-background-clip: text;
  color: transparent;
  margin-bottom: 8px;
  font-family: 'Segoe UI', 'Fira Code', monospace;
}

.title {
  font-size: 18px;
  font-weight: 500;
  color: #2c7a7b;
  border-left: 4px solid #2dd4bf;
  padding-left: 12px;
  margin-bottom: 20px;
  letter-spacing: 0.5px;
}

.divider {
  height: 2px;
  background: radial-gradient(circle, #2dd4bf 30%, transparent 80%);
  margin: 14px 0 16px 0;
}

.info-row {
  display: flex;
  align-items: center;
  gap: 14px;
  margin-bottom: 16px;
  font-size: 15px;
  color: #1e3a4d;
  font-weight: 500;
  font-family: 'Segoe UI', 'Roboto', monospace;
  flex-wrap: wrap;
}

.info-icon {
  width: 28px;
  text-align: center;
  color: #1f7b8c;
}

.info-text {
  word-break: break-all;
  letter-spacing: 0.2px;
}

.skill-tags {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  margin: 20px 0 12px 0;
}

.skill-tag {
  background: #eef2f5;
  padding: 6px 14px;
  border-radius: 40px;
  font-size: 12px;
  font-weight: 600;
  color: #115e67;
  font-family: monospace;
  letter-spacing: 0.3px;
  border: 1px solid #cbd5e1;
  backdrop-filter: blur(2px);
}

.badge-text {
  font-size: 11px;
  color: #6c9a9f;
  margin-top: 6px;
}

.back-header {
  display: flex;
  align-items: center;
  gap: 14px;
  margin-bottom: 26px;
}

.back-icon {
  background: #0f2b3d;
  width: 52px;
  height: 52px;
  border-radius: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
}

.back-title {
  font-size: 22px;
  font-weight: 700;
  color: #0f2b3d;
}

.quote {
  font-style: italic;
  background: #f0f7fa;
  padding: 16px 18px;
  border-radius: 28px;
  margin: 18px 0;
  font-size: 14px;
  border-left: 5px solid #2dd4bf;
  color: #15575e;
  font-family: 'Segoe UI', monospace;
}

.exp-section {
  margin: 8px 0 0 0;
}

.social-links {
  display: flex;
  flex-wrap: wrap;
  gap: 20px;
  margin-top: 20px;
}

.social-item {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  background: #f8fafc;
  padding: 6px 14px;
  border-radius: 50px;
  border: 1px solid #cbd5e6;
}

.footer-note {
  font-size: 10px;
  text-align: center;
  margin-top: 24px;
  color: #6c868e;
  border-top: 1px dashed #cbd5e1;
  padding-top: 14px;
}

@media (max-width: 560px) {
  .card-inner {
    padding: 24px 20px;
  }
  .name {
    font-size: 24px;
  }
  .info-row {
    font-size: 13px;
  }
  .skill-tag {
    font-size: 10px;
  }
}

.business-card-container {
  user-select: none;
}
</style>